<template>
  <v-card outlined class="block">
    <h3>{{ $t('hedgeOS.dashboard.portBasisPanel.title') }} <span class="meta">{{ $t('hedgeOS.dashboard.portBasisPanel.meta') }}</span></h3>

    <div v-for="row in basisRows" :key="row.city" class="basis-row">
      <div class="loc">
        <span class="city">{{ row.city }}</span>
        <span class="prod">{{ $t(`hedgeOS.dashboard.portBasisPanel.products.${row.prodKey}`) }}</span>
      </div>
      <div class="basis-vis">
        <div class="center"></div>
        <div class="dot" :class="{ neg: row.negative }" :style="{ left: row.dotLeft + '%' }"></div>
        <div v-if="row.axis" class="axis">
          <span>-200c</span>
          <span>0</span>
          <span>+200c</span>
        </div>
      </div>
      <span :style="{ color: row.negative ? 'var(--hot)' : 'var(--cool)', 'font-weight': 500 }">{{ row.value }}</span>
    </div>

    <h3 style="margin-top:2.5rem;">{{ $t('hedgeOS.dashboard.portBasisPanel.marginTitle') }} <span class="meta">{{ $t('hedgeOS.dashboard.portBasisPanel.marginMeta') }}</span></h3>
    <div class="margin-decomp">
      <div v-for="row in marginRows" :key="row.labelKey" class="margin-row">
        <span style="color:var(--dim);">{{ $t(`hedgeOS.dashboard.portBasisPanel.marginRows.${row.labelKey}`) }}</span>
        <span :class="row.tone">{{ row.valueKey ? $t(`hedgeOS.dashboard.portBasisPanel.marginRows.${row.valueKey}`) : row.value }}</span>
      </div>
      <div class="margin-row total">
        <span style="font-family:'Fraunces',serif;font-size:1.1rem;font-weight:600;">{{ $t('hedgeOS.dashboard.portBasisPanel.netMargin') }}</span>
        <span class="pos" style="font-size:1.2rem;font-weight:600;">R$ 14.80 / sc</span>
      </div>
    </div>
</v-card>
</template>

<script>
export default {
  name: "PortBasis",
  data() {
    return {
      basisRows: [
        { city: "Paranaguá", prodKey: "soyPr", dotLeft: 71, value: "+ 82c", negative: false, axis: true },
        { city: "Santos", prodKey: "soySp", dotLeft: 68, value: "+ 71c", negative: false },
        { city: "Rio Grande", prodKey: "soyRs", dotLeft: 62, value: "+ 48c", negative: false },
        { city: "Sorriso", prodKey: "soyMt", dotLeft: 32, value: "- 72c", negative: true },
        { city: "Rondonópolis", prodKey: "cornMt", dotLeft: 28, value: "- 88c", negative: true },
        { city: "LME · Aluminum", prodKey: "lme3mCash", dotLeft: 58, value: "2,418", negative: false },
      ],
      marginRows: [
        { labelKey: "cbotFuture", value: "R$ 75.42", tone: "" },
        { labelKey: "basisParanagua", value: "+ R$ 5.90", tone: "pos" },
        { labelKey: "fx", valueKey: "fxConverted", tone: "muted-value" },
        { labelKey: "freight", value: "- R$ 38.20", tone: "neg" },
        { labelKey: "operational", value: "- R$ 4.10", tone: "neg" },
        { labelKey: "productionCost", value: "- R$ 24.22", tone: "neg" },
      ],
    };
  },
};
</script>

<style scoped>
.block {
  background: var(--ink);
  padding: 2rem;
}

.block h3 {
  font-family: 'Fraunces', serif;
  font-weight: 600;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 1.5rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.block h3 .meta {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.65rem;
  color: var(--muted);
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.basis-row {
  display: grid;
  grid-template-columns: 1fr 2fr auto;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--line);
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
}

.basis-row:last-child {
  border-bottom: none;
}

.basis-row .loc {
  display: flex;
  flex-direction: column;
}

.basis-row .loc .city {
  font-family: 'Fraunces', serif;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: -0.01em;
}

.basis-row .loc .prod {
  color: var(--muted);
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-top: 0.2rem;
}

.basis-vis {
  position: relative;
  height: 40px;
  border-left: 1px solid var(--line);
  border-right: 1px solid var(--line);
}

.basis-vis .center {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--dim);
}

.basis-vis .dot {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--cool);
  box-shadow: 0 0 12px var(--cool);
}

.basis-vis .dot.neg {
  background: var(--hot);
  box-shadow: 0 0 12px var(--hot);
}

.basis-vis .axis {
  position: absolute;
  bottom: -15px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  color: var(--muted);
}

.margin-decomp {
  padding: 1rem 0;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
}

.margin-row {
  display: grid;
  grid-template-columns: 1fr auto;
  padding: 0.6rem 0;
  border-bottom: 1px solid var(--line);
}

.margin-row.total {
  padding: 1rem 0;
  border-top: 2px solid var(--hot);
  border-bottom: none;
  margin-top: 0.4rem;
}

.pos {
  color: var(--cool);
}

.neg {
  color: var(--hot);
}

.muted-value {
  color: var(--muted);
}
</style>
